/**
 * Generates a cryptographically secure UUID v4 string.
 * Uses `crypto.randomUUID()` where available, falling back to `crypto.getRandomValues()`
 * for environments that support the Web Crypto API but not `randomUUID` directly.
 * Returns an empty string if neither method is available.
 * @returns {string} A UUID v4 string (e.g. "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx"), or an empty string if
 * the Web Crypto API is unavailable.
 */
export const generateUUID = (): string => {
    if (globalThis.crypto?.randomUUID) {
        return globalThis.crypto.randomUUID();
    }

    if (globalThis.crypto?.getRandomValues) {
        const bytes = new Uint8Array(16);
        globalThis.crypto.getRandomValues(bytes);
        bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
        bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant bits

        return [
            bytes.slice(0, 4),
            bytes.slice(4, 6),
            bytes.slice(6, 8),
            bytes.slice(8, 10),
            bytes.slice(10, 16),
        ]
            .map((seg) =>
                Array.from(seg)
                    .map((b) => b.toString(16).padStart(2, "0"))
                    .join(""),
            )
            .join("-");
    }

    return "";
};
